import React, { useState, useEffect } from "react";
import { Dialog, Slide, Box } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import "./index.scss";

const STORAGE_KEY = "siki_welcome_seen";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const SoundwaveBar = ({ height, delay }) => (
  <div
    className="we-bar"
    style={{ height, animationDelay: `${delay}s` }}
  />
);

const bars = [
  { h: 24, d: 0 }, { h: 48, d: 0.1 }, { h: 36, d: 0.2 }, { h: 64, d: 0.05 },
  { h: 80, d: 0.15 }, { h: 56, d: 0.25 }, { h: 72, d: 0.08 }, { h: 44, d: 0.18 },
  { h: 60, d: 0.12 }, { h: 80, d: 0.22 }, { h: 52, d: 0.06 }, { h: 68, d: 0.16 },
  { h: 40, d: 0.28 }, { h: 56, d: 0.1 }, { h: 32, d: 0.2 }, { h: 48, d: 0.14 },
  { h: 72, d: 0.04 }, { h: 60, d: 0.24 }, { h: 44, d: 0.09 }, { h: 36, d: 0.19 },
  { h: 52, d: 0.02 }, { h: 28, d: 0.13 },
];

export default function WelcomeExplainer() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const seen = localStorage.getItem(STORAGE_KEY);
    if (!seen) {
      localStorage.setItem(STORAGE_KEY, "true");
      setOpen(true);
    }
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        className: "we-modal",
      }}
      BackdropProps={{
        className: "we-backdrop",
      }}
    >
      {/* Hero */}
      <div className="we-hero">
        <div className="we-hero-bg" />
        <div className="we-ring" />
        <div className="we-ring" />
        <div className="we-ring" />

        <div className="we-soundwave">
          {bars.map((b, i) => (
            <SoundwaveBar key={i} height={b.h} delay={b.d} />
          ))}
        </div>

        <div className="we-badge">
          <div className="we-badge-dot" />
          Music Royalties
        </div>

        <button className="we-close" onClick={handleClose}>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="we-content">
        <div className="we-tagline">
          Stream. Go Live. <span>Earn Music Royalties.</span>
        </div>
        <div className="we-subtitle">Every play pays artists directly</div>

        {/* Flow: Add Music → Go Live → Earn */}
        <div className="we-flow">
          <div className="we-flow-step">
            <div className="we-flow-icon">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <div className="we-flow-label">Add Music</div>
            <div className="we-flow-sub">Build your playlist</div>
          </div>

          <div className="we-flow-arrow">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </div>

          <div className="we-flow-step">
            <div className="we-flow-icon">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="we-flow-label">Go Live</div>
            <div className="we-flow-sub">On platform or OBS</div>
          </div>

          <div className="we-flow-arrow">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </div>

          <div className="we-flow-step">
            <div className="we-flow-icon">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="we-flow-label">Earn Royalties</div>
            <div className="we-flow-sub">Every viewer = a stream</div>
          </div>
        </div>

        {/* Math strip */}
        <div className="we-math">
          <div className="we-math-label">Every viewer counts as a stream</div>
          <div className="we-math-row">
            <div className="we-math-item">
              <div className="we-math-val">10</div>
              <div className="we-math-lbl">Viewers</div>
            </div>
            <div className="we-math-op">&times;</div>
            <div className="we-math-item">
              <div className="we-math-val">12</div>
              <div className="we-math-lbl">Songs</div>
            </div>
            <div className="we-math-op">=</div>
            <div className="we-math-item">
              <div className="we-math-val we-math-gold">120</div>
              <div className="we-math-lbl">Streams</div>
            </div>
          </div>
          <div className="we-math-note">Per live session</div>
        </div>

        <button className="we-cta" onClick={handleClose}>
          Start Exploring
        </button>
      </div>
    </Dialog>
  );
}
